import { Group, Select, Stack, Text, Title } from "@mantine/core";
import {
  Alert,
  Button,
  EmptyState,
  LoadingScreen,
  PageHeader,
} from "@xplatform/design-system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { MODULES, type Module, type PublicReleaseNote } from "@models/releaseNotes";
import { useReleaseNotes } from "@hooks/useReleaseNotes";
import { ReleaseNoteCard } from "@components/ReleaseNoteCard";
import { useLocalized } from "@utils/localizedText";

function isModule(value: string | null): value is Module {
  return value !== null && (MODULES as readonly string[]).includes(value);
}

/**
 * Groups by module, newest first inside each group.
 *
 * The API already returns the list ordered by release date descending, so this
 * preserves that order rather than re-sorting. Sorting here on the version
 * string would put 2026.10 below 2026.9.
 */
function groupByModule(
  notes: PublicReleaseNote[],
): Array<[Module, PublicReleaseNote[]]> {
  const groups = new Map<Module, PublicReleaseNote[]>();
  for (const note of notes) {
    const existing = groups.get(note.module);
    if (existing) existing.push(note);
    else groups.set(note.module, [note]);
  }
  return Array.from(groups.entries());
}

export const ReleasesPage = () => {
  const { t } = useTranslation();
  const { formatDate } = useLocalized();
  // Filter state lives in the URL so a support conversation can link straight
  // to one module's history.
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("module");
  const selected = isModule(raw) ? raw : undefined;

  const { data, loading, error, reload } = useReleaseNotes(selected);

  const groups = useMemo(() => groupByModule(data ?? []), [data]);

  const moduleOptions = useMemo(
    () =>
      MODULES.map((module) => ({
        value: module,
        label: t(`releases.module.${module}`),
      })),
    [t],
  );

  const handleFilter = (value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("module", value);
    else next.delete("module");
    setSearchParams(next, { replace: true });
  };

  return (
    <Stack gap="lg" p="lg">
      <PageHeader
        title={t("releases.page.title")}
        description={t("releases.page.description")}
      />

      <Group justify="space-between" align="flex-end">
        <Select
          label={t("releases.page.filterLabel")}
          placeholder={t("releases.page.allModules")}
          data={moduleOptions}
          value={selected ?? null}
          onChange={handleFilter}
          clearable
          w={260}
        />
        {data && (
          <Text size="sm" c="dimmed">
            {t("releases.page.count", { count: data.length })}
          </Text>
        )}
      </Group>

      {loading && <LoadingScreen message={t("common.loading")} />}

      {/* An error state, never a blank page: the user must be able to tell a
          broken backend from a module that has no releases yet. */}
      {!loading && error && (
        <Alert color="red" title={t("releases.page.errorTitle")}>
          <Stack gap="sm" align="flex-start">
            <Text size="sm">{t("releases.page.errorBody")}</Text>
            <Button size="xs" variant="light" onClick={reload}>
              {t("common.retry")}
            </Button>
          </Stack>
        </Alert>
      )}

      {!loading && !error && groups.length === 0 && (
        <EmptyState
          title={t("releases.page.emptyTitle")}
          description={t("releases.page.emptyBody")}
        />
      )}

      {!loading &&
        !error &&
        groups.map(([module, notes]) => (
          <Stack key={module} gap="md">
            <Group gap="sm" align="baseline">
              <Title order={3}>{t(`releases.module.${module}`)}</Title>
              <Text size="sm" c="dimmed">
                {t("releases.page.latest", {
                  date: formatDate(notes[0].released_at),
                })}
              </Text>
            </Group>
            {notes.map((note) => (
              <ReleaseNoteCard key={`${note.module}-${note.version}`} note={note} />
            ))}
          </Stack>
        ))}
    </Stack>
  );
};
